import * as React from 'react'
import {
  Spacing,
  BorderRadius,
  Colors,
  InlineChildren,
  StackChildren,
  Text,
  Stack,
} from '@doordash/design-language'
import styled from 'styled-components'

export enum CardTypes {
  PeakPay = 'peak_pay',
  HourlyPay = 'hourly_pay',
  Challenge = 'challenge',
  Static = 'static',
  Default = 'default',
}

export type CardContent = {
  title: string
  content: string
  icon?: React.ReactNode
  type: CardTypes
}

export enum CardStyleVariants {
  LargeTitle = 'LargeTitle',
  List = 'List',
}

const CardElement = styled.div`
  display: flex;
  padding: ${Spacing.Small}px;
  border-radius: ${BorderRadius.Small}px;
  background-color: ${Colors.BannerHighlightBackground};

  ${({ heightPx }: { heightPx?: number }) =>
    heightPx ? `height: ${heightPx}px;` : ``}
`
type StyledCardCommonProps = {
  card: CardContent
  heightPx?: number
  shouldDisplayIcons?: boolean
}

const LargeTitleCard: React.FC<StyledCardCommonProps> = ({
  card,
  heightPx,
  shouldDisplayIcons,
}) => {
  const { title, content, icon } = card
  return (
    <CardElement heightPx={heightPx}>
      <InlineChildren
        justifyContent={InlineChildren.Justification.SpaceBetween}
        alignItems={InlineChildren.Alignment.Center}
      >
        <StackChildren size={StackChildren.Sizes.XxSmall}>
          <Text styles={Text.Styles.Caption1} color={Text.Colors.TextSecondary}>
            {title}
          </Text>
          <Text styles={Text.Styles.PageTitle2}>{content}</Text>
          <Stack size={Stack.Sizes.Large} />
        </StackChildren>
        {shouldDisplayIcons && icon}
      </InlineChildren>
    </CardElement>
  )
}

const ListCard: React.FC<StyledCardCommonProps> = ({
  card,
  heightPx,
  shouldDisplayIcons,
}) => {
  const { title, content, icon } = card
  return (
    <CardElement heightPx={heightPx}>
      <InlineChildren
        justifyContent={InlineChildren.Justification.SpaceBetween}
        style={{ width: '100%' }}
      >
        <StackChildren size={StackChildren.Sizes.XxSmall} style={{ flex: 0.9 }}>
          <Text styles={Text.Styles.Label2Emphasis}>{title}</Text>
          <Text styles={Text.Styles.Body2}>{content}</Text>
        </StackChildren>
        {shouldDisplayIcons && <div style={{ flex: 0.1 }}>{icon}</div>}
      </InlineChildren>
    </CardElement>
  )
}

type CardProps = {
  card: CardContent
  styleVariant: CardStyleVariants
  heightPx?: number
  shouldDisplayIcons?: boolean
}

export const Card: React.FC<CardProps> = ({
  styleVariant,
  heightPx,
  ...props
}) => {
  switch (styleVariant) {
    case CardStyleVariants.LargeTitle:
      return <LargeTitleCard heightPx={heightPx} {...props} />
    case CardStyleVariants.List:
      return <ListCard heightPx={heightPx} {...props} />
    default:
      return <ListCard heightPx={heightPx} {...props} />
  }
}
